import { StackActions, useNavigation } from '@react-navigation/native';
import { Box } from 'native-base';
import React, { useCallback, useEffect, useState } from 'react';

import { ButtonSheet } from '~/components/button';
import { CardDivider } from '~/components/card';
import { Color } from '~/components/color';
import { ModalPicker } from '~/components/modal-picker';
import { Text } from '~/components/text';
import { FormattedMessage, useIntl } from '~/contexts/intl';
import { CalendarEvent } from '~/types';
import { isWeb } from '~/utils';

import { AppointmentNavigation } from './appointment';
import { AppointmentCancel } from './appointment-cancel';
import { AppointmentModify } from './appointment-modify';

export type AppointmentActionsModalProps = {
    calendarEvent: CalendarEvent;
    cancelCalendarEvent: () => Promise<CalendarEvent | undefined>;
    visible: boolean;
    closeModal: () => void;
};

type AppointmentActionsMode = 'actions' | 'cancel' | 'modify';

export function AppointmentActionsModal(props: AppointmentActionsModalProps) {
    const { calendarEvent, cancelCalendarEvent, visible, closeModal } = props;
    const { formatMessage } = useIntl();

    const { dispatch } = useNavigation<AppointmentNavigation>();

    const [modifyInProgress, setModifyInProgress] = useState<boolean>(false);
    const [cancelInProgress, setCancelInProgress] = useState<boolean>(false);

    const mode: AppointmentActionsMode = cancelInProgress ? 'cancel' : modifyInProgress ? 'modify' : 'actions';

    const isCancellable = calendarEvent.isCancellable ?? false;
    const isModifiable = calendarEvent.isModifiable ?? false;

    useEffect(() => {
        if (visible) {
            setCancelInProgress(false);
            setModifyInProgress(false);
        }
    }, [visible]);

    const openCancel = useCallback(() => {
        setCancelInProgress(true);
    }, []);

    const handleModifyCalendarEvent = useCallback(async () => {
        // The no-op branch (missing ids) does not reset modifyInProgress; that reset relies on
        // navigation-away / handleDismiss / onModalHide. Relevant if the modify flow is re-enabled.
        if (calendarEvent?.id && calendarEvent?.calendarReservationAccess?.id) {
            dispatch(
                StackActions.replace('appointments', {
                    calendarReservationAccessId: calendarEvent.calendarReservationAccess.id,
                    calendarEventId: calendarEvent.id
                })
            );
        }
    }, [calendarEvent, dispatch]);

    const handleCancelCalendarEvent = useCallback(async () => {
        try {
            await cancelCalendarEvent();
        } finally {
            closeModal();
        }
    }, [cancelCalendarEvent, closeModal]);

    const handleDismiss = useCallback(() => {
        if (cancelInProgress) {
            setCancelInProgress(false);
        } else if (modifyInProgress) {
            setModifyInProgress(false);
        } else {
            closeModal();
        }
    }, [cancelInProgress, modifyInProgress, closeModal]);

    return (
        <ModalPicker
            visible={visible}
            contentKey={mode}
            bottomPadding={mode === 'cancel' ? isCancellable : mode === 'modify' ? isModifiable : false}
            extraStyles={
                isWeb()
                    ? {
                          width: 343,
                          marginLeft: 'auto',
                          marginRight: 'auto'
                      }
                    : undefined
            }
            onModalHide={() => {
                setCancelInProgress(false);
                setModifyInProgress(false);
            }}
            renderContent={() => {
                switch (mode) {
                    case 'cancel':
                        return (
                            <AppointmentCancel
                                cancellable={isCancellable}
                                onCancelCalendarEvent={handleCancelCalendarEvent}
                            />
                        );
                    case 'modify':
                        return (
                            <AppointmentModify
                                modifiable={isModifiable}
                                onModifyCalendarEvent={handleModifyCalendarEvent}
                            />
                        );
                    default:
                        return (
                            <Box w="100%">
                                <Text.PARAGRAPH_3
                                    after={4}
                                    style={{
                                        color: Color.SMOOTHIE,
                                        marginBottom: 10,
                                        marginTop: 9,
                                        textAlign: 'center'
                                    }}
                                >
                                    <FormattedMessage id="appointment.cancel" />
                                </Text.PARAGRAPH_3>
                                <Text.CAPTION style={{ textAlign: 'center' }}>
                                    {formatMessage('appointment.modify.cancellation-hours')}
                                </Text.CAPTION>
                                <Text.CAPTION style={{ marginBottom: 22, textAlign: 'center' }}>
                                    {formatMessage('appointment.modify.new-appointment-after-cancellation')}
                                </Text.CAPTION>
                                <CardDivider />
                                <ButtonSheet
                                    trailingSeparator={false}
                                    carets
                                    items={[
                                        // Disable modifications until Acute issue is resolved
                                        // {
                                        //     key: 'modify-appointment',
                                        //     label: formatMessage('appointment.modify'),
                                        //     onPress: openModify
                                        // },
                                        {
                                            key: 'show-cancel-terms',
                                            label: formatMessage('appointment.cancel'),
                                            onPress: openCancel,
                                            color: Color.WARNING_DEFAULT
                                        }
                                    ]}
                                />
                            </Box>
                        );
                }
            }}
            renderActions={() => null}
            onCancel={handleDismiss}
        />
    );
}
